import React, { useMemo } from 'react';
import { Card, Table, Typography, Alert } from 'antd';

const { Text } = Typography;

/**
 * Mix Recipe Calculator
 *
 * Renders a two-column table — Chemical | Dose for this mix — listing
 * water first followed by each chemical with its computed dose for the
 * current tank fill. Stakeholder direction (Damien, round-4 follow-up):
 * the rate/basis columns and the prescriptive fill steps added more
 * complexity than the operator needed at the tractor; the operator just
 * wants the sprayer's shopping list.
 *
 * Rate interpretation:
 *   - rate_unit values of 'g' / 'mL' (or strings containing "100L") are
 *     treated as a per-100L concentration: dose = rate × (water_quantity / 100)
 *     — the spray-job form's dropdown labels these as "g / 100L" /
 *     "mL / 100L" so the per-100L semantics are implicit.
 *   - rate_unit values containing "per_hectare" or "/ha" are treated as a
 *     per-hectare loading: surface a note since we can't compute without
 *     job area.
 *   - Anything else is treated as a literal total (e.g. "2 L" of product).
 */
/**
 * @param {object} props
 * @param {object} props.job - The spray job (chemicals + default water_quantity)
 * @param {number} [props.waterQuantityOverride] - Optional tank size for THIS
 *   mix in litres. Used by the Refill action's modal so the doses recompute
 *   for the refill's litres (which may differ from the initial fill).
 * @param {boolean} [props.embedded] - When true, drops the Card wrapper so the
 *   table can be slotted inside another container (e.g. a modal body).
 */
const MixRecipe = ({ job, waterQuantityOverride, embedded = false }) => {
  const waterQuantity = waterQuantityOverride != null
    ? Number(waterQuantityOverride) || 0
    : Number(job?.water_quantity) || 0;
  const chemicals = job?.chemicals || [];

  const rows = useMemo(() => {
    const out = [{
      key: '__water__',
      chemical: 'Water',
      dose: waterQuantity > 0 ? `${waterQuantity} L` : '—',
    }];
    chemicals.forEach((c) => {
      const name = c.asset?.title || c.asset?.name || 'Unknown chemical';
      const batch = c.asset?.chemical_batch_number;
      const rate = Number(c.quantity);
      const unit = (c.rate_unit || '').toLowerCase();
      const doseUnit = c.rate_unit || '';
      const isPer100L = unit === 'g' || unit === 'ml'
        || unit.includes('100l') || unit.includes('per_100l');
      let doseText;
      if (!Number.isFinite(rate)) {
        doseText = 'Rate not specified';
      } else if (isPer100L) {
        const dose = waterQuantity > 0 ? rate * (waterQuantity / 100) : null;
        doseText = dose != null
          ? `${Math.round(dose * 100) / 100} ${doseUnit}`.trim()
          : '—';
      } else if (unit.includes('hectare') || unit.includes('ha')) {
        doseText = `${rate} per hectare — multiply by area`;
      } else {
        doseText = `${rate} ${doseUnit}`.trim();
      }
      out.push({
        key: c.id ?? `${name}-${batch ?? ''}`,
        chemical: batch ? `${name} (batch ${batch})` : name,
        dose: doseText,
      });
    });
    return out;
  }, [chemicals, waterQuantity]);

  const columns = [
    { title: 'Chemical', dataIndex: 'chemical', key: 'chemical' },
    {
      title: 'Dose for this mix',
      dataIndex: 'dose',
      key: 'dose',
      render: (v) => <Text strong>{v}</Text>,
    },
  ];

  if (!waterQuantity && chemicals.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        message="No recipe to calculate"
        description="Add chemicals and a water quantity to the job to see the mix recipe."
      />
    );
  }

  const table = (
    <Table
      size="middle"
      columns={columns}
      dataSource={rows}
      pagination={false}
    />
  );
  if (embedded) return table;
  return (
    <Card title="Mix Recipe" style={{ marginBottom: 16 }}>
      {table}
    </Card>
  );
};

export default MixRecipe;
